import React from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';

type HastLikeNode = {
  parent?: {
    tagName?: string;
  };
};

const getChildText = (children: React.ReactNode): string => {
  if (typeof children === 'string' || typeof children === 'number') {
    return String(children);
  }

  if (Array.isArray(children)) {
    return children.map(getChildText).join('');
  }

  if (React.isValidElement<{ children?: React.ReactNode }>(children)) {
    return getChildText(children.props.children);
  }

  return '';
};

const isInlineCodeNode = (node: unknown): boolean => {
  if (!node || typeof node !== 'object') return true;

  const parent = (node as HastLikeNode).parent;
  return parent?.tagName !== 'pre';
};

const renderGpSummaryItem = (text: string) => (
  <div className="gp-summary-item" role="listitem">
    <span className="gp-summary-item-bullet" aria-hidden>
      •
    </span>
    <span className="gp-summary-item-text">{text}</span>
  </div>
);

const createGpSummaryComponents = (): Components => ({
  p: ({ children }) => {
    const text = getChildText(children).trim();

    if (/^[a-z]\.\s/i.test(text)) {
      return <p className="member-ai-markdown-p gp-summary-subsection">{children}</p>;
    }

    if (text.startsWith('- ')) {
      return renderGpSummaryItem(text.slice(2));
    }

    return <p className="member-ai-markdown-p">{children}</p>;
  },
  strong: ({ children }) => <strong className="member-ai-markdown-strong">{children}</strong>,
  em: ({ children }) => <em className="member-ai-markdown-em">{children}</em>,
  ul: ({ children }) => (
    <ul className="member-ai-markdown-ul gp-summary-items">
      {children}
    </ul>
  ),
  ol: ({ children, start, node }) => {
    const startValue =
      typeof start === 'number'
        ? start
        : (node?.properties as { start?: number } | undefined)?.start;

    return (
      <ol className="member-ai-markdown-ol" start={startValue}>
        {children}
      </ol>
    );
  },
  li: ({ children }) => {
    const text = getChildText(children).trim();

    if (text.startsWith('- ')) {
      return renderGpSummaryItem(text.slice(2));
    }

    return <li className="member-ai-markdown-li">{children}</li>;
  },
  code: ({ children, node }) => {
    const isInline = isInlineCodeNode(node);
    const text = getChildText(children).trim();

    if (isInline && text.startsWith('- ')) {
      return renderGpSummaryItem(text.slice(2));
    }

    return (
      <code
        className={`member-ai-markdown-code${isInline ? '' : ' member-ai-markdown-code--block'}`}
      >
        {children}
      </code>
    );
  },
  pre: ({ children }) => <pre className="member-ai-markdown-pre">{children}</pre>,
  h1: ({ children }: { children?: React.ReactNode }) => (
    <h1 className="member-ai-markdown-h1">{children}</h1>
  ),
  h2: ({ children }: { children?: React.ReactNode }) => (
    <h2 className="member-ai-markdown-h2">{children}</h2>
  ),
  h3: ({ children }: { children?: React.ReactNode }) => (
    <h3 className="member-ai-markdown-h3">{children}</h3>
  ),
});

const MONITORING_SECTION_TITLES = [
  'Patient overview',
  'Priority areas to monitor',
  'Biomarker monitoring plan',
  'Imaging / scan follow-up',
  'Recommended monitoring schedule',
  'Lifestyle & nutrition focus',
  'Red flags requiring prompt review',
  'Data quality & gaps',
  'Identified data gaps',
] as const;

const escapeRegExp = (value: string): string =>
  value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const stripMarkdownBold = (content: string): string => content.replace(/\*\*/g, '');

const splitMonitoringSectionText = (
  raw: string
): { title: string; body: string | null } => {
  const cleaned = raw.replace(/^\d+\.\s+/, '').trim();

  for (const title of MONITORING_SECTION_TITLES) {
    const match = cleaned.match(
      new RegExp(`^${escapeRegExp(title)}\\s*[:.\\-–—]?\\s*(.*)$`, 'i')
    );
    if (!match) continue;

    const rest = (match[1] || '').trim();
    if (!rest) return { title, body: null };
    return { title, body: rest };
  }

  return { title: '', body: cleaned };
};

const normalizeMonitoringPlanMarkdown = (content: string): string => {
  let text = stripMarkdownBold(content);

  for (const title of MONITORING_SECTION_TITLES) {
    text = text.replace(
      new RegExp(
        `(^|\\n)(\\d+\\.\\s+)?(${escapeRegExp(title)})\\s+([^\\n]{12,})`,
        'gi'
      ),
      (_match, lead: string, num: string | undefined, heading: string, rest: string) =>
        `${lead}${num || ''}${heading}\n\n${rest}`
    );
  }

  return text;
};

const createMonitoringPlanComponents = (): Components => ({
  p: ({ children }) => {
    const text = getChildText(children).trim();

    if (/^[a-z]\.\s+\S/i.test(text)) {
      return <p className="monitoring-plan-subsection-title">{children}</p>;
    }

    if (/^insufficient data to recommend/i.test(text)) {
      return <p className="member-ai-markdown-p monitoring-plan-muted">{children}</p>;
    }

    return <p className="member-ai-markdown-p">{children}</p>;
  },
  strong: ({ children }) => <span className="member-ai-markdown-strong">{children}</span>,
  em: ({ children }) => <span className="member-ai-markdown-em">{children}</span>,
  ul: ({ children }) => <ul className="member-ai-markdown-ul">{children}</ul>,
  ol: ({ children, start, node }) => {
    const startValue =
      typeof start === 'number'
        ? start
        : (node?.properties as { start?: number } | undefined)?.start;

    return (
      <ol className="member-ai-markdown-ol" start={startValue}>
        {children}
      </ol>
    );
  },
  li: ({ children }) => {
    const childArray = React.Children.toArray(children).filter((child) => {
      if (typeof child === 'string') return child.trim().length > 0;
      return true;
    });

    if (childArray.length === 0) {
      return <li className="member-ai-markdown-li" />;
    }

    const [first, ...rest] = childArray;

    if (
      React.isValidElement(first) &&
      typeof first.props === 'object' &&
      first.props !== null &&
      typeof (first.props as { className?: string }).className === 'string' &&
      (first.props as { className: string }).className.includes('member-ai-markdown-ul')
    ) {
      return <li className="member-ai-markdown-li">{children}</li>;
    }

    const rawText =
      React.isValidElement<{ children?: React.ReactNode }>(first)
        ? getChildText(first.props.children).trim()
        : typeof first === 'string'
          ? first.trim()
          : getChildText(first).trim();

    const { title, body } = splitMonitoringSectionText(rawText);
    const isKnownSection = MONITORING_SECTION_TITLES.some(
      (sectionTitle) => sectionTitle.toLowerCase() === title.toLowerCase()
    );

    if (!isKnownSection) {
      return <li className="member-ai-markdown-li">{children}</li>;
    }

    return (
      <li className="member-ai-markdown-li">
        <div className="monitoring-plan-section-title">{title}</div>
        {body ? <p className="member-ai-markdown-p">{body}</p> : null}
        {rest}
      </li>
    );
  },
  code: ({ children, node }) => {
    const isInline = isInlineCodeNode(node);

    return (
      <code
        className={`member-ai-markdown-code${isInline ? '' : ' member-ai-markdown-code--block'}`}
      >
        {children}
      </code>
    );
  },
  pre: ({ children }) => <pre className="member-ai-markdown-pre">{children}</pre>,
  h1: ({ children }: { children?: React.ReactNode }) => (
    <h1 className="member-ai-markdown-h1">{children}</h1>
  ),
  h2: ({ children }: { children?: React.ReactNode }) => (
    <h2 className="member-ai-markdown-h2">{children}</h2>
  ),
  h3: ({ children }: { children?: React.ReactNode }) => (
    <h3 className="member-ai-markdown-h3">{children}</h3>
  ),
});

const baseMarkdownComponents: Components = {
  p: ({ children }) => <p className="member-ai-markdown-p">{children}</p>,
  strong: ({ children }) => <strong className="member-ai-markdown-strong">{children}</strong>,
  em: ({ children }) => <em className="member-ai-markdown-em">{children}</em>,
  ul: ({ children }) => <ul className="member-ai-markdown-ul">{children}</ul>,
  ol: ({ children, start, node }) => {
    const startValue =
      typeof start === 'number'
        ? start
        : (node?.properties as { start?: number } | undefined)?.start;

    return (
      <ol className="member-ai-markdown-ol" start={startValue}>
        {children}
      </ol>
    );
  },
  li: ({ children }) => <li className="member-ai-markdown-li">{children}</li>,
  code: ({ children, node }) => {
    const isInline = isInlineCodeNode(node);

    return (
      <code
        className={`member-ai-markdown-code${isInline ? '' : ' member-ai-markdown-code--block'}`}
      >
        {children}
      </code>
    );
  },
  pre: ({ children }) => <pre className="member-ai-markdown-pre">{children}</pre>,
  h1: ({ children }: { children?: React.ReactNode }) => (
    <h1 className="member-ai-markdown-h1">{children}</h1>
  ),
  h2: ({ children }: { children?: React.ReactNode }) => (
    <h2 className="member-ai-markdown-h2">{children}</h2>
  ),
  h3: ({ children }: { children?: React.ReactNode }) => (
    <h3 className="member-ai-markdown-h3">{children}</h3>
  ),
};

type AiMarkdownContentProps = {
  content: string;
  className?: string;
  variant?: 'default' | 'gp-summary' | 'monitoring-plan';
};

const AiMarkdownContent = ({ content, className, variant = 'default' }: AiMarkdownContentProps) => {
  const components =
    variant === 'gp-summary'
      ? createGpSummaryComponents()
      : variant === 'monitoring-plan'
        ? createMonitoringPlanComponents()
        : baseMarkdownComponents;

  const renderedContent =
    variant === 'monitoring-plan' ? normalizeMonitoringPlanMarkdown(content) : content;

  return (
    <div className={className}>
      <ReactMarkdown components={components}>{renderedContent}</ReactMarkdown>
    </div>
  );
};

export default AiMarkdownContent;
